import { AppSyncMessage, CloudEvent, NotificationPayload } from './ws-types';

// Track processed event IDs to prevent duplicates
const processedEventIds = new Set<string>();
// Limit the size of the cache to prevent memory leaks
const MAX_CACHE_SIZE = 1000;

/**
 * Processes WebSocket messages and handles different CloudEvent types.
 * Assumes the "event" field on the message contains a CloudEvent.
 * @param message The raw message received from AppSync
 */
export function handleWebSocketMessage(message: AppSyncMessage): void {
    // Proceed only if this is a "data" type message and an event exists
    if (message.type !== 'data' || !message.event) {
        return;
    }

    try {
        // Parse the incoming event as a CloudEvent
        const event: CloudEvent = JSON.parse(message.event);

        // Check for event ID and skip if we've already processed this event
        if (event.id && processedEventIds.has(event.id)) {
            console.log(`Skipping duplicate event with ID: ${event.id}`);
            return;
        }

        // Store the event ID in our set to prevent reprocessing
        if (event.id) {
            // If we're nearing the max cache size, remove oldest elements (10% of cache)
            if (processedEventIds.size >= MAX_CACHE_SIZE) {
                const idsToRemove = Array.from(processedEventIds).slice(0, MAX_CACHE_SIZE / 10);
                idsToRemove.forEach(id => processedEventIds.delete(id));
            }
            processedEventIds.add(event.id);
        }

        // Handle events by type
        switch (event.type) {
            // All firewall-related events trigger a complete UI refresh
            // This ensures all data stays up-to-date for the user
            case 'com.safetycli.telemetry.refresh':
            case 'com.safetycli.firewall.configured':
            case 'com.safetycli.firewall.disabled':
            case 'com.safetycli.firewall.heartbeat':
                console.log(`[Firewall] ${event.type.split('.').pop()} event received`);
                dispatchFirewallEvent('firewall:refresh-events', {
                    timestamp: new Date(event.time).toLocaleString(),
                    eventType: event.type,
                    eventId: event.id
                });
                break;

            // Data events (for test compatibility)
            case 'data':
                console.log('[Data Event]');
                break;

            // Auth events
            case 'auth':
            case 'com.safetycli.auth':
                handleAuthEvent(event.data as NotificationPayload);
                break;

            // Scan creation events also trigger a refresh to update relevant UI elements
            case 'com.safetycli.scan_created':
                handleNotification(event.data as NotificationPayload);
                dispatchFirewallEvent('firewall:refresh-events', {
                    timestamp: new Date(event.time).toLocaleString(),
                    eventType: event.type,
                    eventId: event.id
                });
                break;

            // General notifications
            case 'notify':
                handleNotification(event.data as NotificationPayload);
                break;

            // Unknown event types
            default:
                console.log(`Unhandled event type: ${event.type}`);
                break;
        }
    } catch (error) {
        console.error('Error processing WebSocket message:', error);
    }
}

/**
 * Helper function to clear the processed events cache
 * Useful for testing or manual cache management
 * @param silent When true, doesn't log a message (useful for tests)
 */
export function clearProcessedEvents(silent: boolean = false): void {
    processedEventIds.clear();
    if (!silent) {
        console.log('Processed events cache cleared');
    }
}

/**
 * Dispatches a custom event for firewall components to handle.
 * Used to communicate from WebSocket handlers to Alpine.js UI components.
 * @param eventName Name of the event to dispatch (e.g., 'firewall:refresh-events')
 * @param detail Event details object containing relevant event information
 */
function dispatchFirewallEvent(eventName: string, detail: any): void {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

/**
 * Handles notification events from the WebSocket.
 * Creates a standardized notification that can be displayed in the UI.
 * Also handles special event types like scan creation.
 * @param payload The notification data from the WebSocket event
 */
function handleNotification(payload: NotificationPayload): void {
    console.log('[Notification]', {
        type: payload.type,
        message: payload.message,
        duration: payload.display_duration
    });
    const event = new CustomEvent('notify', {
        bubbles: true,
        detail: {
            type: payload.type,
            content: payload.message,
            displayDuration: payload.display_duration,
            action: payload.action,
            actionText: payload.action_text,
            secondaryAction: payload.secondary_action,
            secondaryActionText: payload.secondary_action_text
        }
    });
    window.dispatchEvent(event);

    if (payload.action === "scan_created") {
        console.log('[Onboarding] Scan created event received.');
        window.dispatchEvent(new CustomEvent('onboarding:scan-created'));
    }
}

/**
 * Handles authentication events from the WebSocket.
 * Processes CLI authentication and triggers onboarding completion when appropriate.
 * @param payload The authentication data from the WebSocket event
 */
function handleAuthEvent(payload: NotificationPayload): void {
    if (payload.data) {
        console.log('[Auth Event]', {
            method: payload.data.method,
            email: payload.data.email,
            organization_name: payload.data.organization_name,
            organization_id: payload.data.organization_id
        });

        const userEmail = document.getElementById('content-wrapper')?.getAttribute('data-user-email');
        if (payload.data.method === "cli" && payload.data.email === userEmail) {
            console.log('[Onboarding] Safety CLI authenticated for current user.');
            window.dispatchEvent(new CustomEvent('onboarding:install-complete'));
        }
    }
}
